import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient, clearTokens, ensureCsrfToken, setAuthFlag, isAuthenticated as checkAuthCookie } from '@/lib/api-client';
import { clearCachedApiResponses } from '@/lib/offline/service-worker-cache';
import {
  clearSessionApiResponseCache,
  clearSessionOfflineData,
  confirmOfflineSessionOwner,
  confirmDiscardOfflineWork,
  getOfflineDiscardWarning,
  getOfflineSessionOwner,
  registerOfflineDiscardConfirmer,
  setOfflineSessionOwner,
  type DiscardReason,
  type OfflineWorkCount,
} from '@/lib/offline/session-offline-data';
import { clearMFAEnrollmentAccount, setMFAEnrollmentAccount } from '@/lib/mfa-enrollment-account';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/app/components/ui/alert-dialog';
import { User, UserRole, ModuleRolesMap, LegalAcceptanceStatus, UserMembership } from '@/lib/types';

interface AuthUser {
  id: number;
  username: string;
  email: string;
  role: UserRole | null;
  is_staff: boolean;
  organization: number | null;
  phone: string | null;
  emergency_contact: string | null;
  job_title: string | null;
  department: string | null;
  person_id: number | null;
  has_signing_pin: boolean;
  module_roles: Partial<ModuleRolesMap>;
  can_manage_jobs?: boolean;
  can_edit_board?: boolean;
  can_dispatch_board?: boolean;
  can_manage_onboarding?: boolean;
  can_complete_onboarding?: boolean;
  can_admin_work?: boolean;
  can_resolve_jsa_revalidation?: boolean;
  can_record_inspections?: boolean;
  legal_acceptance?: LegalAcceptanceStatus;
}

interface MFAChallenge {
  mfa_required: true;
  mfa_token: string;
}

interface LoginResponse {
  mfa_required?: boolean;
  mfa_token?: string;
  must_change_password?: boolean;
  mfa_enrollment_required?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<AuthUser | MFAChallenge | null>;
  verifyMFA: (mfaToken: string, code: string, isBackupCode?: boolean) => Promise<AuthUser | null>;
  logout: () => Promise<boolean>;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [membershipInactive, setMembershipInactive] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState<(OfflineWorkCount & { reason: DiscardReason }) | null>(null);
  const queryClient = useQueryClient();
  const lastOrgRef = useRef<number | null>(null);
  const userRef = useRef<AuthUser | null>(null);
  const discardResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const lastHeldRefreshRef = useRef<number>(0);
  const heldRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHeldRefreshTimer = useCallback(() => {
    if (heldRefreshTimerRef.current !== null) {
      clearTimeout(heldRefreshTimerRef.current);
      heldRefreshTimerRef.current = null;
    }
  }, []);

  const applyUser = useCallback((nextUser: AuthUser | null) => {
    setOfflineSessionOwner(nextUser
      ? { userId: nextUser.id, orgId: nextUser.organization ?? null }
      : null);
    setUser(nextUser);
  }, []);

  const resolveDiscardPrompt = useCallback((confirmed: boolean) => {
    discardResolverRef.current?.(confirmed);
    discardResolverRef.current = null;
    setDiscardPrompt(null);
  }, []);

  useEffect(() => registerOfflineDiscardConfirmer((info, reason) => new Promise((resolve) => {
    discardResolverRef.current?.(false);
    discardResolverRef.current = resolve;
    setDiscardPrompt({ ...info, reason });
  })), []);

  useEffect(() => () => {
    discardResolverRef.current?.(false);
    discardResolverRef.current = null;
  }, []);

  // Keep a stable ref to the current user so callbacks that should not list
  // `user` in their dependency arrays can still access the latest value.
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const fetchUserProfile = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const response = await apiClient.get<User>('/api/v1/profile/');
      const data = response.data;
      return { ...data, module_roles: data.module_roles ?? {} };
    } catch (error) {
      // Treat genuine auth failures as "not authenticated".  Rate-limits,
      // network errors, and 5xx responses must not be confused with logout
      // triggers; those are surfaced to the caller instead.
      if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        if (error.response.data?.code === 'membership_inactive') {
          setMembershipInactive(true);
          return userRef.current;
        }
        return null;
      }
      throw error;
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    if (!checkAuthCookie()) {
      applyUser(null);
      setMembershipInactive(false);
      setIsLoading(false);
      return null;
    }

    try {
      const userData = await fetchUserProfile();
      if (userData) {
        setMembershipInactive(false);
        setMFAEnrollmentAccount(userData.email || userData.username);
        const previousOrg = lastOrgRef.current;
        const nextOrg = userData.organization ?? null;
        if (previousOrg !== null && nextOrg !== previousOrg) {
          await clearCachedApiResponses();
          queryClient.clear();
        }
        lastOrgRef.current = nextOrg;
        await ensureCsrfToken();
      }
      applyUser(userData);
      clearHeldRefreshTimer();
      if (userData) void confirmOfflineSessionOwner({
        userId: userData.id,
        orgId: userData.organization ?? null,
      });
      return userData;
    } catch {
      // Non-auth errors (network, 429 after retry, 5xx) should not wipe an
      // already-validated session or force a redirect to /login.
      // Keep the current user state and stop loading.
    } finally {
      setIsLoading(false);
    }
    return userRef.current;
  }, [applyUser, clearHeldRefreshTimer, fetchUserProfile, queryClient]);

  const login = useCallback(async (username: string, password: string): Promise<AuthUser | MFAChallenge | null> => {
    clearMFAEnrollmentAccount();
    setMembershipInactive(false);
    const previousOwner = getOfflineSessionOwner();
    await confirmOfflineSessionOwner(null);
    let response: { data: LoginResponse };
    try {
      response = await apiClient.post<LoginResponse>('/api/v1/auth/login/', { username, password });
    } catch (error) {
      setOfflineSessionOwner(previousOwner);
      throw error;
    }

    // If MFA is required, return the challenge (no cookies are set yet)
    if (response.data?.mfa_required) {
      return { mfa_required: true, mfa_token: response.data.mfa_token } as MFAChallenge;
    }

    // No MFA — backend set httpOnly cookies
    setAuthFlag();
    await clearCachedApiResponses();
    queryClient.clear();
    // The backend may require a forced password change. Avoid calling profile
    // in that case because ForcePasswordChangeMiddleware rejects it with 403.
    if (response.data?.must_change_password) {
      applyUser(null);
      setIsLoading(false);
      window.location.href = '/change-password';
      return null;
    }
    if (response.data?.mfa_enrollment_required) {
      setMFAEnrollmentAccount(username);
      applyUser(null);
      setIsLoading(false);
      window.location.href = '/enroll-mfa';
      return null;
    }
    try {
      const userData = await fetchUserProfile();
      if (userData) {
        setMFAEnrollmentAccount(userData.email || userData.username);
        lastOrgRef.current = userData.organization ?? null;
      }
      applyUser(userData);
      return userData;
    } catch {
      applyUser(null);
      return null;
    }
  }, [applyUser, fetchUserProfile, queryClient]);

  const verifyMFA = useCallback(async (mfaToken: string, code: string, isBackupCode = false): Promise<AuthUser | null> => {
    const payload = isBackupCode
      ? { mfa_token: mfaToken, backup_code: code }
      : { mfa_token: mfaToken, totp_code: code };
    const response = await apiClient.post('/api/v1/auth/mfa/verify/', payload);
    setMembershipInactive(false);
    setAuthFlag();
    await clearCachedApiResponses();
    queryClient.clear();
    // The backend may require a forced password change. Avoid calling profile
    // in that case because ForcePasswordChangeMiddleware rejects it with 403.
    if (response.data?.must_change_password) {
      applyUser(null);
      setIsLoading(false);
      window.location.href = '/change-password';
      return null;
    }
    try {
      const userData = await fetchUserProfile();
      if (userData) {
        setMFAEnrollmentAccount(userData.email || userData.username);
        lastOrgRef.current = userData.organization ?? null;
      }
      applyUser(userData);
      return userData;
    } catch {
      applyUser(null);
      return null;
    }
  }, [applyUser, fetchUserProfile, queryClient]);

  const logout = useCallback(async () => {
    clearHeldRefreshTimer();
    if (!(await confirmDiscardOfflineWork('logout'))) return false;
    const logoutRequest = apiClient.post('/api/v1/auth/logout/');
    // Wipe React state and org-scoped cache first so callers that navigate
    // immediately (without awaiting) cannot render the previous tenant's data.
    applyUser(null);
    setMembershipInactive(false);
    lastOrgRef.current = null;
    clearMFAEnrollmentAccount();
    queryClient.clear();
    const cacheClear = clearSessionApiResponseCache();
    await Promise.allSettled([logoutRequest, cacheClear, clearSessionOfflineData()]);
    // Clear tokens (including CSRF) AFTER the logout request so the
    // POST can still attach the X-CSRFToken header.
    // Pass the active QueryClient so tests that provide their own instance
    // clear the same cache that the UI uses.
    clearTokens(queryClient);
    return true;
  }, [applyUser, clearHeldRefreshTimer, queryClient]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('sso') === 'success') {
      setAuthFlag();
    }
    setOfflineSessionOwner(null);
    const id = requestAnimationFrame(() => { refreshUser(); });
    return () => cancelAnimationFrame(id);
  }, [refreshUser]);

  useEffect(() => {
    const handleOnline = () => {
      void refreshUser();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [refreshUser]);

  useEffect(() => {
    const handleTermsAcceptanceRequired = () => {
      void refreshUser();
    };
    window.addEventListener(
      'terms-acceptance-required',
      handleTermsAcceptanceRequired,
    );
    return () => {
      window.removeEventListener(
        'terms-acceptance-required',
        handleTermsAcceptanceRequired,
      );
    };
  }, [refreshUser]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const serviceWorker = navigator.serviceWorker;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'OFFLINE_SESSION_OWNER_HELD') return;
      const now = Date.now();
      const elapsed = now - lastHeldRefreshRef.current;
      if (elapsed >= 30_000) {
        clearHeldRefreshTimer();
        lastHeldRefreshRef.current = now;
        void refreshUser();
        return;
      }
      if (heldRefreshTimerRef.current !== null) return;
      heldRefreshTimerRef.current = setTimeout(() => {
        heldRefreshTimerRef.current = null;
        lastHeldRefreshRef.current = Date.now();
        void refreshUser();
      }, 30_000 - elapsed);
    };
    try {
      serviceWorker.addEventListener('message', handleMessage);
    } catch {
      return;
    }
    return () => {
      try {
        serviceWorker.removeEventListener('message', handleMessage);
      } catch {
        return;
      } finally {
        clearHeldRefreshTimer();
      }
    };
  }, [clearHeldRefreshTimer, refreshUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user || (membershipInactive && checkAuthCookie()),
    isLoading,
    login,
    verifyMFA,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AlertDialog
        open={discardPrompt !== null}
        onOpenChange={(open) => {
          if (!open) resolveDiscardPrompt(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsynced offline work?</AlertDialogTitle>
            <AlertDialogDescription>
              {discardPrompt && getOfflineDiscardWarning(discardPrompt)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolveDiscardPrompt(false)}>
              {discardPrompt?.reason === 'switch-org' ? 'Cancel' : 'Stay signed in'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => resolveDiscardPrompt(true)}>
              {discardPrompt?.reason === 'switch-org' ? 'Discard and switch' : 'Discard and sign out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AuthContext.Provider>
  );
}

interface MembershipInactiveDetail {
  organizationId: number | null;
  organizationName: string;
}

export function MembershipInactiveHandler() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<MembershipInactiveDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);
  const handledRef = useRef(false);

  useEffect(() => {
    const handleMembershipInactive = (event: Event) => {
      if (
        location.pathname === '/select-organization' ||
        location.pathname === '/login'
      ) {
        return;
      }
      if (handledRef.current) return;
      const detail = (event as CustomEvent<MembershipInactiveDetail>).detail;
      if (!detail) return;
      handledRef.current = true;
      setError(null);
      setOrganization(detail);
    };
    window.addEventListener('membership-inactive', handleMembershipInactive);
    return () => window.removeEventListener('membership-inactive', handleMembershipInactive);
  }, [location.pathname]);

  const handleContinue = async () => {
    if (!organization || isContinuing) return;
    setIsContinuing(true);
    try {
      const response = await apiClient.get<UserMembership[]>('/api/v1/my-memberships/');
      const hasAlternative = response.data.some(
        (membership) => membership.organization_id !== organization.organizationId,
      );
      if (hasAlternative) {
        navigate('/select-organization', { replace: true });
      } else {
        if (!(await logout())) return;
        navigate('/login', { replace: true });
      }
      setOrganization(null);
      setError(null);
      handledRef.current = false;
    } catch {
      setError('Unable to check your organization access. Please try again.');
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <AlertDialog open={organization !== null}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Organization access changed</AlertDialogTitle>
          <AlertDialogDescription>
            You no longer have access to {organization?.organizationName}
          </AlertDialogDescription>
          {error && <p role="alert">{error}</p>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            disabled={isContinuing}
            onClick={(event) => {
              event.preventDefault();
              void handleContinue();
            }}
          >
            {error ? 'Retry' : 'Continue'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
